import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../api";
import axios from "axios";
import { SavedQueryGroup } from "../interfaces/queryBuilderInterfaces";
import qs from "qs";
import { act } from "react";

export const saveQuery = createAsyncThunk<Number, SavedQueryGroup>(
  "savegroup/save",
  async (payload) => {
    let response = await axiosInstance.post(`/rest/savedquery/save`, payload);
    return response.data;
  },
);

export const updateExistingQuery = createAsyncThunk<Number, SavedQueryGroup>(
  "savegroup/update",
  async (payload) => {
    let response = await axiosInstance.post(
      `/rest/savedquery/update/${payload.id}`,
      payload,
    );
    return response.data;
  },
);

export const fetchSavedQueries = createAsyncThunk(
  "savegroup/list",
  async () => {
    const response = await axiosInstance.get(`/rest/savedquery/groupQueries`);
    return response.data;
  },
);

export const fetchSharedQueries = createAsyncThunk(
  "sharequery/list",
  async () => {
    const response = await axiosInstance.get(`/rest/sharequery/groupQueries`);
    return response.data;
  },
);

export const fetchLegacyTipNetQueries = createAsyncThunk(
  "tipnet/legacyQueries",
  async ({ corp, email }: { corp: string; email: string }) => {
    const response = await axios.get(
      `/tipnet-api/rest/api/query/${corp}/${email}`,
      { headers: { "tip-api-key": import.meta.env.VITE_TIPNET_API_KEY } },
    );
    const data = response.data;
    return Array.isArray(data) ? data : [data];
  },
);

export const fetchLegacyTipNetQueryDetail = createAsyncThunk(
  "tipnet/legacyQueryDetail",
  async ({ corp, email, savedId }: { corp: string; email: string; savedId: number }) => {
    const response = await axios.get(
      `/tipnet-api/rest/api/query/${corp}/${email}/${savedId}`,
      { headers: { "tip-api-key": import.meta.env.VITE_TIPNET_API_KEY } },
    );
    return response.data;
  },
);

export const loadQuery = createAsyncThunk<
  SavedQueryGroup,
  { groupId: string; name: string }
>("loadgroup", async ({ groupId, name }) => {
  let response = await axiosInstance.get(
    `/rest/savedquery/chart/query/copy/${groupId}/${name}`,
  );
  return response.data;
});

export const updateQuery = createAsyncThunk<
  boolean,
  { groupId: number; form: any }
>("updategroup", async ({ groupId, form }) => {
  let response = await axiosInstance.post(
    `/rest/savedquery/group/update/${groupId}`,
    qs.stringify(form),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );
  return response.data;
});

export const nameExists = createAsyncThunk<
  boolean,
  { groupId: number; form: any }
>("nameExists", async ({ groupId, form }) => {
  let response = await axiosInstance.post(
    `/rest/savedquery/checkNameExists/${groupId}`,
    qs.stringify(form),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    },
  );
  return response.data;
});

export const queryExists = createAsyncThunk<boolean, { groupId: number }>(
  "queryExists",
  async ({ groupId }) => {
    let response = await axiosInstance.get(
      `/rest/savedquery/checkQueryExists/${groupId}`,
    );
    return response.data;
  },
);

export const deleteQuery = createAsyncThunk<boolean, { form: any }>(
  "deletegroup",
  async ({ form }) => {
    let response = await axiosInstance.delete(`/rest/savedquery/group/delete`, {
      data: qs.stringify(form),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  },
);

const initialState: any = {
  groupId: null,
  loading: false,
  error: null,
  savedQueries: [],
  sharedQueries: [],
  legacyTipNetQueries: [],
  legacyTipNetQueryDetail: null,
  pendingLegacyQuery: null,
  savedQueryName: [],
  savePopUpTitle: "Save New",
  queryIds: [],
};

const saveSlice = createSlice({
  name: "save",
  initialState,
  reducers: {
    saveGroupId: (state, action) => {
      state.groupId = action.payload;
    },
    saveQueryId: (state, action) => {
      state.queryId = action.payload;
    },
    saveQueryIds: (state, action) => {
      state.queryIds = action.payload;
    },
    addNewQueryId: (state) => {
      state.savedQueryName.push(state.savedQueryName[0]);
      state.queryIds.push(null);
    },
    setSavedQueryName: (state, action) => {
      state.savedQueryName[action.payload.index] = action.payload.name;
    },
    setSavePopUpTitle: (state, action) => {
      state.savePopUpTitle = action.payload;
    },
    setSaveQueryNameToAll: (state, action) => {
      state.savedQueryName = action.payload;
    },
    removeQueryId(state, action) {
      state.queryIds.splice(action.payload, 1);
    },
    setPendingLegacyQuery(state, action) {
      state.pendingLegacyQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveQuery.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveQuery.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload !== -1) {
          state.groupId = action.payload;
        }
      })
      .addCase(saveQuery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || null;
      })
      .addCase(fetchSavedQueries.fulfilled, (state, action) => {
        state.savedQueries = action.payload;
      })
      .addCase(fetchSharedQueries.fulfilled, (state, action) => {
        state.sharedQueries = action.payload;
      })
      .addCase(fetchLegacyTipNetQueries.fulfilled, (state, action) => {
        state.legacyTipNetQueries = action.payload;
      })
      .addCase(fetchLegacyTipNetQueryDetail.fulfilled, (state, action) => {
        state.legacyTipNetQueryDetail = action.payload;
      });
  },
});

export const {
  saveQueryId,
  saveGroupId,
  setSavedQueryName,
  setSavePopUpTitle,
  saveQueryIds,
  addNewQueryId,
  removeQueryId,
  setSaveQueryNameToAll,
  setPendingLegacyQuery,
} = saveSlice.actions;
export default saveSlice.reducer;
